/** Credential administration: logins and machine keys.
 *
 * The endpoints have existed since the first release and this console never called one of them,
 * so creating a colleague's login, deactivating a departing employee or revoking a compromised
 * API key all meant dropping to curl (docs/KNOWN-PROBLEMS.md finding 18, FRONTEND-AUDIT F-P1-1).
 * Revoking a leaked key is an incident-response action; needing a terminal and the right
 * bearer token to do it is an operational gap, not a missing convenience.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as adminApi from "../../api/admin.api.js";
import { DataTable } from "../../components/DataTable.js";
import { Modal } from "../../components/Modal.js";
import { PlusIcon, TrashIcon } from "../../components/Icon.js";
import { useToast } from "../../components/Toast.js";
import { operatorMessage } from "../../lib/api-message.js";
import { formatTimestamp } from "../../lib/format.js";
import type { CreatedApiKey, Role } from "../../types/api.js";

const ROLES: ReadonlyArray<{ value: Role; label: string; note: string }> = [
  { value: "view_only", label: "view_only", note: "doors, status and audit. No control at all." },
  { value: "door_operator", label: "door_operator", note: "+ MOMENTARY open. Cannot hold a door." },
  {
    value: "security_admin",
    label: "security_admin",
    note: "+ hold-lock, hold-unlock, release-override — the PERSISTENT overrides.",
  },
  { value: "super_admin", label: "super_admin", note: "+ registry and system administration." },
];

export function UsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKey, setNewKey] = useState<CreatedApiKey | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({ queryKey: ["users"], queryFn: adminApi.listUsers });
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: adminApi.listApiKeys });

  const createUser = useMutation({
    mutationFn: adminApi.createUser,
    onMutate: () => setError(null),
    onSuccess: (user) => {
      setShowCreateUser(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.show(`${user.displayName} can now sign in.`);
    },
    onError: (err: Error) =>
      setError(operatorMessage(err, "Unable to create that user. Please try again.")),
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.setUserActive(id, isActive),
    onMutate: () => setError(null),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.show(
        user.isActive
          ? `${user.displayName} was reactivated.`
          : `${user.displayName} was deactivated and can no longer sign in.`,
      );
    },
    onError: (err: Error) => {
      const message = operatorMessage(err, "Unable to change that account. Please try again.");
      setError(message);
      toast.show(message, "critical");
    },
  });

  const createKey = useMutation({
    mutationFn: adminApi.createApiKey,
    onMutate: () => setError(null),
    onSuccess: (created) => {
      setShowCreateKey(false);
      setError(null);
      // Shown ONCE. Only the hash is stored, so this is the single moment it exists anywhere
      // the operator can read it. No toast here on purpose - the modal that carries the secret
      // is the confirmation, and a second message would only compete with it.
      setNewKey(created);
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (err: Error) =>
      setError(operatorMessage(err, "Unable to create that key. Please try again.")),
  });

  const revokeKey = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => adminApi.revokeApiKey(id),
    onMutate: () => setError(null),
    // The name comes from the mutation variables rather than from `confirmRevoke`, which this
    // handler clears - reading it back afterwards would race the state update.
    onSuccess: (_result, key) => {
      setConfirmRevoke(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast.show(`${key.name} was revoked and will no longer authenticate.`);
    },
    onError: (err: Error) => {
      const message = operatorMessage(err, "Unable to revoke that key. Please try again.");
      setError(message);
      toast.show(message, "critical");
    },
  });

  const userList = users.data ?? [];
  const keyList = keys.data ?? [];

  /** A message rendered on the page BEHIND a dialog is a message nobody reads. When a dialog is
   * open its own copy is shown inside it instead. */
  const modalOpen = showCreateUser || showCreateKey || newKey !== null || confirmRevoke !== null;

  function dismissModals() {
    setShowCreateUser(false);
    setShowCreateKey(false);
    setConfirmRevoke(null);
    setError(null);
  }

  return (
    <>
      <div className="page-head">
        <h1 id="page-heading" tabIndex={-1}>
          Users and API keys
        </h1>
      </div>

      <p className="page-intro">
        Who can sign in to this console, and which machine credentials exist. Everything on this
        screen is recorded in the audit log against your name.
      </p>

      {error && !modalOpen ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{error}</div>
        </div>
      ) : null}

      <div className="page-head">
        <h2>People</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setError(null);
            setShowCreateUser(true);
          }}
        >
          <PlusIcon aria-hidden="true" focusable="false" />
          Add a user
        </button>
      </div>

      <DataTable
        caption="User accounts that can sign in to this console"
        isLoading={users.isLoading}
        isError={users.isError}
        isEmpty={userList.length === 0}
        loadingMessage="Loading users…"
        errorMessage="The user list could not be loaded."
        emptyMessage="No users exist. That should be impossible — the service refuses to start with nobody able to sign in."
        columns={
          <>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Role</th>
            <th scope="col">State</th>
            <th scope="col">Last sign-in</th>
            <th scope="col">Actions</th>
          </>
        }
      >
        {userList.map((user) => (
          <tr key={user.id}>
            <td className="cell-name">{user.displayName}</td>
            <td className="mono">{user.email}</td>
            <td className="mono">{user.role}</td>
            <td>
              <span className={user.isActive ? "status tone-ok" : "status tone-muted"}>
                <span className="status-icon" aria-hidden="true">
                  {user.isActive ? "✓" : "·"}
                </span>
                <span>{user.isActive ? "active" : "inactive"}</span>
              </span>
            </td>
            <td className="cell-sub">{formatTimestamp(user.lastLoginAt)}</td>
            <td>
              <button
                type="button"
                className="btn"
                disabled={setActive.isPending}
                aria-label={`${user.isActive ? "Deactivate" : "Reactivate"} ${user.displayName}`}
                title={`${user.isActive ? "Deactivate" : "Reactivate"} ${user.displayName}`}
                onClick={() => setActive.mutate({ id: user.id, isActive: !user.isActive })}
              >
                {user.isActive ? "Deactivate" : "Reactivate"}
              </button>
            </td>
          </tr>
        ))}
      </DataTable>

      <div className="page-head">
        <h2>API keys</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setError(null);
            setShowCreateKey(true);
          }}
        >
          <PlusIcon aria-hidden="true" focusable="false" />
          Create a key
        </button>
      </div>

      <p className="page-intro">
        A key carries a role exactly as a person does. A key with <code>security_admin</code> can
        hold a door unlocked, so give an integration the smallest role that does its job.
      </p>

      <DataTable
        caption="Machine credentials"
        isLoading={keys.isLoading}
        isError={keys.isError}
        isEmpty={keyList.length === 0}
        loadingMessage="Loading API keys…"
        errorMessage="The API key list could not be loaded."
        emptyMessage="No API keys exist."
        columns={
          <>
            <th scope="col">Name</th>
            <th scope="col">Role</th>
            <th scope="col">Prefix</th>
            <th scope="col">State</th>
            <th scope="col">Last used</th>
            <th scope="col">Expires</th>
            <th scope="col">Actions</th>
          </>
        }
      >
        {keyList.map((key) => (
          <tr key={key.id}>
            <td className="cell-name">{key.name}</td>
            <td className="mono">{key.role}</td>
            <td className="mono">{key.keyPrefix}…</td>
            <td>
              <span className={key.isActive ? "status tone-ok" : "status tone-muted"}>
                <span className="status-icon" aria-hidden="true">
                  {key.isActive ? "✓" : "·"}
                </span>
                <span>{key.isActive ? "active" : "revoked"}</span>
              </span>
            </td>
            <td className="cell-sub">{formatTimestamp(key.lastUsedAt)}</td>
            <td className="cell-sub">{key.expiresAt ? formatTimestamp(key.expiresAt) : "never"}</td>
            <td>
              {key.isActive ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  aria-label={`Revoke the API key ${key.name}`}
                  title={`Revoke the API key ${key.name}`}
                  onClick={() => {
                    setError(null);
                    setConfirmRevoke({ id: key.id, name: key.name });
                  }}
                >
                  <TrashIcon aria-hidden="true" focusable="false" />
                  Revoke
                </button>
              ) : (
                <span className="cell-sub">—</span>
              )}
            </td>
          </tr>
        ))}
      </DataTable>

      {showCreateUser ? (
        <CreateUserModal
          busy={createUser.isPending}
          error={error}
          onCancel={dismissModals}
          onSubmit={(input) => createUser.mutate(input)}
        />
      ) : null}

      {showCreateKey ? (
        <CreateKeyModal
          busy={createKey.isPending}
          error={error}
          onCancel={dismissModals}
          onSubmit={(input) => createKey.mutate(input)}
        />
      ) : null}

      {newKey ? <NewKeyModal created={newKey} onClose={() => setNewKey(null)} /> : null}

      {confirmRevoke ? (
        <Modal
          title="Revoke this API key?"
          tone="critical"
          // Not dismissible mid-request: closing over a call that is still going to land leaves
          // the operator unsure whether the key is dead or alive.
          dismissible={!revokeKey.isPending}
          onClose={dismissModals}
          footer={
            <>
              <button
                type="button"
                className="btn"
                disabled={revokeKey.isPending}
                onClick={dismissModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={revokeKey.isPending}
                onClick={() => revokeKey.mutate(confirmRevoke)}
              >
                <TrashIcon aria-hidden="true" focusable="false" />
                {revokeKey.isPending ? "Revoking…" : "Revoke key"}
              </button>
            </>
          }
        >
          {/* Inside the dialog: a page banner here would sit under the backdrop. */}
          {error ? (
            <div className="banner banner-critical" role="alert">
              <span className="banner-icon" aria-hidden="true">
                ✗
              </span>
              <div className="banner-body">{error}</div>
            </div>
          ) : null}

          <p>
            <strong>{confirmRevoke.name}</strong> will stop working immediately and cannot be
            restored. Anything using it — an integration, a script, a camera host — will start
            getting 401 on its very next request.
          </p>
          <p>
            If you are revoking it because it leaked, revoke it anyway: a broken integration is
            recoverable, a leaked key that can hold a door unlocked is not.
          </p>
        </Modal>
      ) : null}
    </>
  );
}

function CreateUserModal({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  /** A refusal from the API, rendered in the dialog rather than on the page behind it. */
  error?: string | null;
  onCancel: () => void;
  onSubmit: (input: { email: string; displayName: string; password: string; role: Role }) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("view_only");
  const [showPassword, setShowPassword] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  function submit() {
    // Checked here so the operator is told before a round trip; the API validates all of it
    // again regardless, because a UI check is not a control.
    if (!email.includes("@")) return setProblem("Enter a valid email address.");
    if (displayName.trim().length === 0) return setProblem("Enter a name.");
    if (password.length < 12) return setProblem("The password must be at least 12 characters.");
    setProblem(null);
    onSubmit({ email: email.trim(), displayName: displayName.trim(), password, role });
  }

  const selected = ROLES.find((r) => r.value === role);

  return (
    <Modal
      title="Add a user"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? "Creating…" : "Create the user"}
          </button>
        </>
      }
    >
      {/* `problem` is this form's own check; `error` is the API's refusal of what was sent. */}
      {(problem ?? error) ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{problem ?? error}</div>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="new-user-name">Name</label>
        <input
          id="new-user-name"
          value={displayName}
          autoComplete="off"
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="new-user-email">Email</label>
        <input
          id="new-user-email"
          type="email"
          value={email}
          autoComplete="off"
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="new-user-password">Initial password</label>
        <div className="input-with-button">
          <input
            id="new-user-password"
            type={showPassword ? "text" : "password"}
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            aria-pressed={showPassword}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <p className="field-hint">
          At least 12 characters. Give it to them over a channel that is not this console, and have
          them change it.
        </p>
      </div>

      <div className="field">
        <label htmlFor="new-user-role">Role</label>
        <select id="new-user-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <p className="field-hint">{selected?.note}</p>
      </div>
    </Modal>
  );
}

function CreateKeyModal({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  /** A refusal from the API, rendered in the dialog rather than on the page behind it. */
  error?: string | null;
  onCancel: () => void;
  onSubmit: (input: { name: string; role: Role; expiresAt?: string | null }) => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("view_only");
  const [expiresInDays, setExpiresInDays] = useState("90");
  const [problem, setProblem] = useState<string | null>(null);

  function submit() {
    if (name.trim().length === 0)
      return setProblem("Name the key after the thing that will use it.");
    setProblem(null);
    const days = Number(expiresInDays);
    const expiresAt =
      Number.isFinite(days) && days > 0
        ? new Date(Date.now() + days * 86_400_000).toISOString()
        : null;
    onSubmit({ name: name.trim(), role, expiresAt });
  }

  const selected = ROLES.find((r) => r.value === role);
  const dangerous = role === "security_admin" || role === "super_admin";

  return (
    <Modal
      title="Create an API key"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? "Creating…" : "Create the key"}
          </button>
        </>
      }
    >
      {/* `problem` is this form's own check; `error` is the API's refusal of what was sent. */}
      {(problem ?? error) ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{problem ?? error}</div>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="new-key-name">Name</label>
        <input id="new-key-name" value={name} onChange={(e) => setName(e.target.value)} />
        <p className="field-hint">
          Name it after what will use it — &ldquo;Reception camera host&rdquo;, not &ldquo;key
          2&rdquo;. It is the only thing that identifies the key in the audit log.
        </p>
      </div>

      <div className="field">
        <label htmlFor="new-key-role">Role</label>
        <select id="new-key-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <p className="field-hint">{selected?.note}</p>
      </div>

      {dangerous ? (
        <div className="banner banner-warning">
          <span className="banner-icon" aria-hidden="true">
            ⚠
          </span>
          <div className="banner-body">
            A <code>{role}</code> key can place a <strong>persistent override</strong> — hold a door
            unlocked or locked indefinitely, with fingerprint and card verification suspended. Give
            an integration the smallest role that does its job.
          </div>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="new-key-expiry">Expires in (days)</label>
        <input
          id="new-key-expiry"
          type="number"
          min="0"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
        />
        <p className="field-hint">0 for a key that never expires. Prefer an expiry.</p>
      </div>
    </Modal>
  );
}

/** The one moment the secret exists anywhere readable. Not dismissible by a stray Escape or a
 * backdrop click: closing this by accident means the key is gone and a new one must be issued. */
function NewKeyModal({ created, onClose }: { created: CreatedApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title="Copy this key now"
      tone="critical"
      dismissible={false}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have stored it
        </button>
      }
    >
      <div className="banner banner-warning">
        <span className="banner-icon" aria-hidden="true">
          ⚠
        </span>
        <div className="banner-body">{created.warning}</div>
      </div>

      <p>
        <strong>{created.name}</strong> — role <code>{created.role}</code>
      </p>

      <pre className="detail-json secret-value">{created.secret}</pre>

      <button
        type="button"
        className="btn"
        onClick={() => {
          // Clipboard access can be refused (an insecure origin, a permissions policy). The
          // key is on screen and selectable either way, so a failure changes nothing important.
          navigator.clipboard
            ?.writeText(created.secret)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
        }}
      >
        {copied ? "Copied ✓" : "Copy to clipboard"}
      </button>

      <p className="field-hint">
        Store it the way this service stores its own secrets — see <code>docs/SECURITY.md</code>. Do
        not paste it into a ticket or a chat message.
      </p>
    </Modal>
  );
}
